import * as React from 'react';
import clsx from 'clsx';
import playButtonIcon from '@/assets/images/svg/play_button_icon.svg';
import pauseIcon from '@/assets/images/svg/pause_icon.svg';
import { echoService } from '@/services/echoService';
import { getErrorMessage } from '@/api/client';
import type { VoiceClone } from '@/types/echo_creation';
import { formatDuration } from '@/utils/formatters';

interface AudioPlayerMockProps {
  draftId?: string;
  echoId?: string;
}

export const AudioPlayerMock: React.FC<AudioPlayerMockProps> = ({ draftId, echoId }) => {
  const [voiceClone, setVoiceClone] = React.useState<VoiceClone | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [audioUrl, setAudioUrl] = React.useState<string | null>(null);
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [duration, setDuration] = React.useState(0);
  const [error, setError] = React.useState<string | null>(null);

  const audioRef = React.useRef<HTMLAudioElement | null>(null);
  const blobUrlRef = React.useRef<string | null>(null);

  // Fetch voice clones on mount
  React.useEffect(() => {
    if (!draftId && !echoId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const fetcher = echoId
      ? echoService.listVoiceClonesByEcho(echoId)
      : echoService.listVoiceClones(draftId ?? '');

    fetcher
      .then((res) => {
        if (cancelled) return;
        const ready = res.voice_clones.find((clone) => clone.status === 'ready');
        setVoiceClone(ready ?? null);
      })
      .catch(() => {
        if (!cancelled) setVoiceClone(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [draftId, echoId]);

  // Cleanup blob URL and audio element on unmount
  React.useEffect(
    () => () => {
      audioRef.current?.pause();
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    },
    []
  );

  const setupAudio = React.useCallback((url: string) => {
    const audio = new Audio(url);

    audio.addEventListener('loadedmetadata', () => setDuration(audio.duration));
    audio.addEventListener('timeupdate', () => setCurrentTime(audio.currentTime));
    audio.addEventListener('ended', () => setIsPlaying(false));
    audio.addEventListener('pause', () => setIsPlaying(false));
    audio.addEventListener('play', () => setIsPlaying(true));

    audioRef.current = audio;
  }, []);

  const handlePlay = async () => {
    if ((!draftId && !echoId) || !voiceClone) return;
    setError(null);

    // If audio already generated, toggle play/pause
    if (audioUrl && audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        await audioRef.current.play();
      }
      return;
    }

    // Generate audio via TTS
    setIsGenerating(true);
    try {
      const blob = echoId
        ? await echoService.testVoiceCloneByEcho(echoId, voiceClone.id)
        : await echoService.testVoiceClone(draftId ?? '', voiceClone.id);
      const url = URL.createObjectURL(blob);
      blobUrlRef.current = url;
      setAudioUrl(url);
      setupAudio(url);

      // Small delay to let Audio element initialise
      await new Promise<void>((resolve) => {
        setTimeout(resolve, 50);
      });
      await audioRef.current?.play();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    audioRef.current.currentTime = ratio * duration;
  };

  const disabled = loading || !voiceClone;
  const progressPct = duration > 0 ? (currentTime / duration) * 100 : 0;

  const buttonIcon = isPlaying ? pauseIcon : playButtonIcon;
  const buttonAlt = isPlaying ? 'Pause' : 'Play';

  return (
    <div className={clsx('flex w-full items-center gap-4', 'rounded-20 bg-white p-4')}>
      {/* Play/Pause Button */}
      <button
        type='button'
        onClick={handlePlay}
        disabled={disabled || isGenerating}
        className={clsx(
          'shrink-0 transition-transform',
          disabled || isGenerating
            ? 'cursor-not-allowed opacity-40'
            : 'hover:scale-105 active:scale-95'
        )}
      >
        {isGenerating ? (
          <div className='flex size-[40px] items-center justify-center'>
            <div className='size-5 animate-spin rounded-full border-2 border-brand-750 border-t-transparent' />
          </div>
        ) : (
          <img src={buttonIcon} alt={buttonAlt} className='size-[40px] object-contain' />
        )}
      </button>

      {/* Content Column */}
      <div className='flex flex-1 flex-col gap-2'>
        {/* Title */}
        <span className='text-left font-sans text-16 font-medium text-dark-100'>
          {disabled ? 'No voice clone available' : 'Test Voice Cloning'}
        </span>

        {/* Progress Bar */}
        <div
          className={clsx(
            'relative h-[6px] w-full overflow-hidden rounded-86 bg-gray-430',
            !disabled && audioUrl && 'cursor-pointer'
          )}
          onClick={handleProgressClick}
        >
          <div
            className='h-full rounded-86 bg-brand-750 transition-[width] duration-100'
            style={{ width: `${progressPct}%` }}
          />
        </div>

        {/* Time + Error */}
        {error ? (
          <span className='truncate text-left font-sans text-12 font-medium leading-21 text-red-500'>
            {error}
          </span>
        ) : (
          <span className='shrink-0 text-left font-sans text-12 font-medium leading-21 text-dark-100'>
            {formatDuration(currentTime)} / {formatDuration(duration)}
          </span>
        )}
      </div>
    </div>
  );
};
